import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardContent, 
  TextField, 
  Button, 
  Typography,
  Container,
  Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase/config';
import LoginImage from "../statics/login.svg"

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSignUp] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const idToken = await user.getIdToken();
          localStorage.setItem('authToken', idToken);
          navigate('/', { replace: true });
        } catch (error) {
          console.error('Error getting ID token:', error);
        }
      }
      setAuthLoading(false);
    });

    return () => unsubscribe();
  }, [navigate]);

  // Don't render login form if still checking auth or if user is authenticated
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#FFFAE9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isSignUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
      // Navigation will be handled by the auth state change listener
    } catch (error) {
      setError(error.message);
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#FFFAE9',
        display: 'flex',
        flexDirection: 'column'
      }}
    >
      <Container
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Card
          sx={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
            width: '100%',
            maxWidth: '400px',
            p: 4
          }}
        >
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2
            }}
          >
            <Box
              component="img"
              src={LoginImage}
              alt="Login Illustration"
              sx={{
                width: '224px',
                height: '160px',
                mb: 2,
                userSelect: 'none'
              }}
              onError={(e) => {
                e.target.src = 'https://placehold.co/224x160/EEEEEE/8A5918?text=Office';
                e.target.onerror = null;
              }}
            />
            
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 'bold',
                mb: 3,
                letterSpacing: '0.05em',
                color: '#8A5918'
              }}
            >
              {isSignUp ? 'Sign Up' : 'Login'}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2
              }}
            >
              <TextField
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FFF1B8',
                    borderRadius: '6px',
                    fontWeight: 500,
                    color: '#374151',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: '2px solid #E0B060'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 16px',
                    '&::placeholder': {
                      color: '#b09c66',
                      opacity: 1
                    }
                  }
                }}
              />

              <TextField
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#FFF1B8',
                    borderRadius: '6px',
                    fontWeight: 500,
                    color: '#374151',
                    '& fieldset': {
                      border: 'none'
                    },
                    '&:hover fieldset': {
                      border: 'none'
                    },
                    '&.Mui-focused fieldset': {
                      border: '2px solid #E0B060'
                    }
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '12px 16px',
                    '&::placeholder': {
                      color: '#b09c66',
                      opacity: 1
                    }
                  }
                }}
              />

              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  backgroundColor: '#8A5918',
                  color: '#FFF1B8',
                  fontWeight: 'bold',
                  py: 1.5,
                  borderRadius: '8px',
                  mt: 1,
                  textTransform: 'none',
                  fontSize: '16px',
                  '&:hover': {
                    backgroundColor: '#A87B30'
                  },
                  '&:disabled': {
                    backgroundColor: '#ccc'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                {loading ? 'Please wait...' : (isSignUp ? 'Sign Up' : 'Login')}
              </Button>

              {/* <Button
                type="button"
                onClick={() => setIsSignUp(!isSignUp)}
                sx={{
                  color: '#8A5918',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline'
                  }
                }}
              >
                {isSignUp ? 'Already have an account? Login' : "Don't have an account? Sign Up"}
              </Button> */}
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Login;
